import PropTypes from 'prop-types'
import { forwardRef, memo, useCallback, useMemo, useState } from 'react'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import Button from '../Button/Button'
import Description from './Description/Description'
import styles from './Dismissible.module.scss'
import Title from './Title/Title'

/**
 * The `Dismissible`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const DismissibleComponent = forwardRef((props, forwardedRef) => {
  const { Tag, children, className, onDismiss, ...rest } = props
  const ref = useFallbackRef(forwardedRef)
  const [isDismissed, setIsDismissed] = useState(false)
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )

  /**
   * Handle click dismiss
   */
  const handleClickDismiss = useCallback(() => {
    onDismiss()
    setIsDismissed(true)
  }, [onDismiss])

  if (isDismissed) {
    return null
  }

  return (
    <Tag ref={ref} className={classNameOutput} {...rest}>
      <div className={styles.display}>{children}</div>
      <div className={styles.close}>
        <Button
          color="j"
          size="xs"
          type="symbol"
          icon="close"
          borderRadius="c"
          onClick={handleClickDismiss}
          useTooltip={true}
          title="Dismiss"
        />
      </div>
    </Tag>
  )
})

// Display name
DismissibleComponent.displayName = 'Dismissible'

/** @type {object} */
DismissibleComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  Tag: PropTypes.elementType,
  onDismiss: PropTypes.func
}

/** @type {object} */
DismissibleComponent.defaultProps = {
  children: null,
  className: '',
  Tag: 'div',
  onDismiss: noop
}

// Memoize
const Dismissible = memo(DismissibleComponent)

// Bind the types
Dismissible.Title = Title
Dismissible.Description = Description

// Export
export default Dismissible
